import { ImageIcon } from 'lucide-react';

type Props = {
  /** 1–6 — must match `offboardingCoverImage()` in `flowIllustrationPaths.ts` */
  step: number;
  /** `purple` = left rail brand gradient (`SplitFlowShell` coverVariant purple) */
  variant?: 'default' | 'purple';
};

/**
 * Shown when `/illustrations/flow/offboard-step-NN.png` is missing or fails to load.
 * Theme-aware dashed frame + exact filename so design can drop in raster assets later.
 */
export function OffboardingFlowCoverPlaceholder({ step, variant = 'default' }: Props) {
  const n = Math.min(6, Math.max(1, Math.floor(step)));
  const filename = `offboard-step-${String(n).padStart(2, '0')}.png`;
  const isPurple = variant === 'purple';

  return (
    <div
      className={
        isPurple
          ? 'flex h-full min-h-0 w-full max-w-[min(100%,440px)] flex-col items-center justify-center p-1'
          : 'mx-auto flex w-full max-w-[min(100%,min(92vw,520px))] flex-col items-center gap-3'
      }
      aria-hidden
    >
      <div
        className={
          isPurple
            ? 'flex max-h-full w-full max-w-full flex-col items-center justify-center rounded-2xl border-2 border-dashed border-white/35 bg-white/[0.08] px-5 py-6 text-center shadow-inner'
            : 'flex min-h-[220px] w-full flex-col items-center justify-center rounded-2xl border-2 border-dashed px-5 py-8 text-center sm:min-h-[260px] border-slate-300/90 bg-slate-200/30 shadow-inner dark:border-slate-600/90 dark:bg-white/[0.04] aspect-[4/3] max-h-[min(52vh,520px)]'
        }
      >
        <div
          className={
            isPurple
              ? 'mb-3 flex h-14 w-14 items-center justify-center rounded-2xl border border-white/30 bg-white/15 text-white/90'
              : 'mb-3 flex h-14 w-14 items-center justify-center rounded-2xl border border-slate-300/80 bg-white/80 text-slate-500 dark:border-white/15 dark:bg-white/[0.06] dark:text-slate-400'
          }
        >
          <ImageIcon className="h-7 w-7" strokeWidth={1.5} />
        </div>
        <p
          className={
            isPurple
              ? 'text-[11px] font-semibold uppercase tracking-wide text-white/80'
              : 'text-[11px] font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400'
          }
        >
          Illustration placeholder
        </p>
        <p
          className={
            isPurple
              ? 'mt-1 font-mono text-[11px] font-medium leading-snug text-white'
              : 'mt-1 font-mono text-[11px] font-medium leading-snug text-slate-800 dark:text-slate-200'
          }
        >
          {filename}
        </p>
        <p
          className={
            isPurple
              ? 'mt-2 max-w-[28ch] text-[10px] leading-relaxed text-white/70'
              : 'mt-2 max-w-[28ch] text-[10px] leading-relaxed text-slate-500 dark:text-slate-500'
          }
        >
          Add PNG under{' '}
          <span className={isPurple ? 'font-mono text-[10px] text-white/85' : 'font-mono text-[10px] text-slate-700 dark:text-slate-400'}>
            public/illustrations/flow/
          </span>
        </p>
      </div>
    </div>
  );
}
